/**
 * As ações da ficha do equipamento (#363): "Registrar inspeção" é a única com a cor primária;
 * "Editar dados", "Marcar como fora de uso" (ou "Reativar equipamento") e "Excluir equipamento"
 * são secundárias. Fora de uso, "Registrar inspeção" fica desabilitada e a tela diz o motivo e
 * o que a reativação faz, em texto (o tooltip sozinho não chega a quem usa o toque).
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

import { RouteActionButton } from '../../../components';
import { ActionButton } from '../../../components/common/ActionButton';
import { pode } from '../../../authorization/pode';
import { DECOMMISSIONED_REASON } from './InspectionHistory';
import { LockableActionButton } from './LockableActionButton';

const TOUCH_TARGET = 44;

export interface EquipmentStatusActionsProps {
  equipmentId: number | string;
  decommissioned: boolean;
  /** Uma chamada ao servidor em andamento: nenhuma ação dispara outra por cima. */
  busy: boolean;
  onRegisterInspection: () => void;
  onToggleDecommission: () => void;
  onDelete: () => void;
}

export const EquipmentStatusActions: React.FC<EquipmentStatusActionsProps> = ({
  equipmentId,
  decommissioned,
  busy,
  onRegisterInspection,
  onToggleDecommission,
  onDelete,
}) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
      <LockableActionButton
        acao="fire_equipment.update"
        rotulo="Registrar inspeção"
        variant="contained"
        color="primary"
        lockedReason={decommissioned ? DECOMMISSIONED_REASON : undefined}
        disabled={busy}
        sx={{ minHeight: TOUCH_TARGET }}
        onClick={onRegisterInspection}
      >
        Registrar inspeção
      </LockableActionButton>
      <RouteActionButton
        para={`/fire-equipments/${equipmentId}/edit`}
        exige={pode('fire_equipment.update')}
        variant="outlined"
        color="inherit"
        sx={{ minHeight: TOUCH_TARGET }}
      >
        Editar dados
      </RouteActionButton>
    </Box>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <ActionButton
        acao="fire_equipment.update"
        rotulo={decommissioned ? 'Reativar equipamento' : 'Marcar como fora de uso'}
        variant="text"
        color="inherit"
        disabled={busy}
        sx={{ minHeight: TOUCH_TARGET }}
        onClick={onToggleDecommission}
      >
        {decommissioned ? 'Reativar equipamento' : 'Marcar como fora de uso'}
      </ActionButton>
      <ActionButton
        acao="fire_equipment.delete"
        rotulo="Excluir equipamento"
        variant="text"
        color="error"
        disabled={busy}
        sx={{ minHeight: TOUCH_TARGET }}
        onClick={onDelete}
      >
        Excluir equipamento
      </ActionButton>
    </Box>

    {decommissioned && (
      <Typography variant="body1" sx={{ mt: 1 }}>
        Equipamento fora de uso: não é possível registrar inspeção. Ao reativar, a próxima inspeção passa a ser hoje;
        confira as validades.
      </Typography>
    )}
  </Box>
);

export default EquipmentStatusActions;
